/**
 * Cache-patch reducers for live events — docs/spec/03 §3.
 *
 * **Patch what is visible; refetch what may have moved.** A claim by another
 * transcriber rewrites the row already in the Query cache, so the change lands
 * with no request and no spinner. But a patch can only rewrite rows that are
 * already there — it cannot add one that has just started matching the filter,
 * or drop one that has stopped. So every `job.*` event ALSO asks the caller for
 * one coalesced list refetch (see `LiveApplyResult`).
 *
 * An earlier revision refetched only on `job.created`, on the assumption that a
 * status change just updates a row in place. It does not: an admin filtered to
 * `transcription_qa_check` never saw a job arrive there (Marc, 2026-07-29).
 *
 * Split out of `ws.ts` (which 01 §2 names as the WS file) because these are pure
 * functions over a `QueryClient` with no transport involved — they are the part
 * worth testing exhaustively without a socket in sight.
 *
 * ## The rule every reducer here follows
 *
 * **Only ever patch what is already cached.** No reducer creates a cache entry.
 * A `job.updated` for a job this operator has never opened must not conjure a
 * half-populated `JobDetail` — the next real fetch would be a cache *hit* on
 * that fiction, and the workspace would render a job with no scan and no
 * history. Absent stays absent; HTTP fills it in when someone asks for it.
 */
import type { QueryClient } from '@tanstack/react-query'
import { queryKeys } from '../lib/queryKeys'
import type { JobDetail, JobListResponse, JobSummary } from './jobs'
import type { LiveEvent } from './ws'

/** Global queue counts, pushed on the `queue` topic (03 §2). */
export interface LiveCounts {
  open: number
  in_progress: number
  qa_check: number
}

/** What the caller still has to do after a reducer ran. */
export interface LiveApplyResult {
  /**
   * The set of jobs matching the visible filter may have changed, so the list
   * must be refetched. **Every `job.*` event sets this.**
   *
   * It is not done in here, and it is not optional either:
   *
   * - **A patch cannot add or remove a row.** `patchJob` rewrites jobs already
   *   cached, so a job moving *into* the current filter is invisible. That was a
   *   real bug (Marc, 2026-07-29): an admin filtered to `transcription_qa_check`
   *   received `job.submitted` for a job that had been in progress, and the job
   *   never appeared — nothing to patch, and no refresh requested.
   * - **The client cannot build the row.** `job.submitted` and friends carry
   *   only `job_id` and `status`. Only `job.created` includes a JobLiteSchema
   *   row, and even that is not spliced: where it sorts depends on the list's
   *   filters, sort and page boundary, so a local insert lands somewhere the
   *   server disagrees with and jumps on the next fetch.
   *
   * The caller owns the timing and coalesces bursts — one refetch per batch, not
   * one per event (05 §5).
   */
  needsListRefresh: boolean
}

const NO_EFFECT: LiveApplyResult = { needsListRefresh: false }
const NEEDS_LIST_REFRESH: LiveApplyResult = { needsListRefresh: true }

/**
 * Fields a live event can change on a cached job row.
 *
 * `Partial<JobSummary>` rather than the three scalars, so the api's complete
 * post-transition row (10 §Q15) can be applied as-is. Merged, never substituted:
 * a `JobLiteSchema` row is not a `JobDetail`, so replacing the detail entry with
 * one would drop the scan and the status history the workspace needs.
 */
type JobPatch = Partial<JobSummary>

/**
 * Rewrite one job wherever it is already cached: its detail entry, and its row
 * in every cached list — under filter combinations this module knows nothing
 * about, which is why it walks `jobs.lists()` as a prefix rather than guessing
 * keys (02 §5).
 */
function patchJob(queryClient: QueryClient, jobId: string, patch: JobPatch): void {
  queryClient.setQueryData<JobDetail>(queryKeys.jobs.detail(jobId), (current) =>
    current === undefined ? current : { ...current, ...patch },
  )

  /**
   * Rebuilt unconditionally, including for the pages holding none of this job —
   * and a `job.claimed` does reach every cached page.
   *
   * That is deliberate: **TanStack applies structural sharing to this return
   * value**, so a new-but-equal object is collapsed back to the previous
   * reference and subscribers do not re-render. An earlier revision tracked a
   * `changed` flag to skip the rebuild; a mutation test (2026-07-29) showed it
   * altered no observable behaviour, and TypeScript cannot even model it —
   * `changed` is assigned inside a callback, so narrowing reports it as always
   * false and lint rejects the branch as dead. Not worth a suppression for an
   * allocation.
   */
  queryClient.setQueriesData<JobListResponse>({ queryKey: queryKeys.jobs.lists() }, (current) => {
    if (current === undefined) return current
    return {
      ...current,
      results: current.results.map((row: JobSummary) =>
        row.id === jobId ? { ...row, ...patch } : row,
      ),
    }
  })
}

/**
 * Apply one live event to the cache.
 *
 * Returns what the caller must still handle rather than doing it here, so the
 * timing policy (coalescing, visibility) lives with the hook that owns it.
 */
export function applyLiveEvent(queryClient: QueryClient, event: LiveEvent): LiveApplyResult {
  switch (event.type) {
    case 'job.claimed':
      /**
       * Prefer the full row. The scalars are the fallback for a deployment that
       * predates 10 §Q15 — and they leave every other column stale, so a queue
       * row would show a new status beside an old "updated" timestamp.
       */
      patchJob(
        queryClient,
        event.job_id,
        event.job ?? {
          status: event.status,
          assignee_id: event.assignee_id,
          assignee_name: event.assignee_name,
        },
      )
      return NEEDS_LIST_REFRESH

    /**
     * `job.submitted` carries either `transcription_qa_check` or
     * `transcription_auto_approved` depending on who submitted (02 §3, 03 §2).
     * It is patched from the message like any other status change — assuming
     * "submitted means QA" is what drifts the admin queue's count upward.
     */
    case 'job.updated':
    case 'job.submitted':
    case 'job.reviewed':
      patchJob(queryClient, event.job_id, event.job ?? { status: event.status })
      return NEEDS_LIST_REFRESH

    case 'counts':
      queryClient.setQueryData<LiveCounts>(queryKeys.live.counts(), {
        open: event.open,
        in_progress: event.in_progress,
        qa_check: event.qa_check,
      })
      return NO_EFFECT

    case 'job.created':
      // The row is in `event.job`, but see `needsListRefresh` above for why it
      // is not spliced into the cached page here.
      return NEEDS_LIST_REFRESH

    case 'presence':
      // Presence is ephemeral per-tab UI state, not server state the cache owns
      // (01 §3). The hook holds it; nothing to patch.
      return NO_EFFECT
  }
}
